// file: src/components/slideshow/ImageSlideshow.jsx
import React, { useState, useEffect, useRef } from 'react';
import styles from './ImageSlideshow.module.css';

// Import images
import FoundryRequirements from '../assets/Foundry_requirements.png';
import FoundryArchitecture from '../assets/Foundry_architecture.png';
import FoundryElectricalDesign from '../assets/Foundry_electrical_design.png';
import FoundryMechanicalDesign from '../assets/Foundry_mechanical_design.png';

const images = [
  FoundryMechanicalDesign,
  FoundryElectricalDesign,
  FoundryArchitecture,
  FoundryRequirements,
];

const captions = [
  "Design & Simulate in 3D",
  "Design & Validate Electronics",
  "Analyze & Visualize Data",
  "Parse & Summarize Documents",
];

/**
 * ImageSlideshow component - Auto-cycles through images with high-tech dissolve transitions
 */
export default function ImageSlideshow() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const imageRefs = useRef([]);
  const isAnimatingRef = useRef(false);
  const typingTimeoutRef = useRef(null);

  // Typing effect for captions
  useEffect(() => {
    // Clear any existing typing animation
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    setIsTyping(true);
    setDisplayText('');
    
    const targetText = captions[currentIndex];
    let currentChar = 0;

    const typeNextChar = () => {
      if (currentChar < targetText.length) {
        setDisplayText(targetText.substring(0, currentChar + 1));
        currentChar++;
        typingTimeoutRef.current = setTimeout(typeNextChar, 50); // 50ms per character
      } else {
        setIsTyping(false);
      }
    };

    // Start typing after a brief delay
    typingTimeoutRef.current = setTimeout(typeNextChar, 200);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [currentIndex]);

  useEffect(() => {
    // Set initial state: hide all except first image
    imageRefs.current.forEach((card, index) => {
      if (card) {
        if (index === 0) {
          card.style.opacity = '1';
          card.style.zIndex = '10';
          card.style.filter = 'blur(0px)';
        } else {
          card.style.opacity = '0';
          card.style.zIndex = '1';
          card.style.filter = 'blur(8px)';
        }
      }
    });

    // Start auto-play - transition every 4 seconds
    const intervalId = setInterval(() => {
      if (isAnimatingRef.current) return;
      
      isAnimatingRef.current = true;
      
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % images.length;
        const currentCard = imageRefs.current[prevIndex];
        const nextCard = imageRefs.current[nextIndex];

        if (!currentCard || !nextCard) {
          isAnimatingRef.current = false;
          return prevIndex;
        }

        // Set next card to be on top
        nextCard.style.zIndex = '10';
        currentCard.style.zIndex = '9';

        // High-tech dissolve transition using CSS transitions
        // Set initial state for next card
        nextCard.style.transition = 'none';
        nextCard.style.opacity = '0';
        nextCard.style.transform = 'scale(1.1)';
        nextCard.style.filter = 'blur(12px)';

        // Force reflow to ensure initial state is applied
        nextCard.offsetHeight;

        // Enable transitions
        currentCard.style.transition = 'opacity 1s cubic-bezier(0.4, 0, 0.2, 1), transform 1s cubic-bezier(0.4, 0, 0.2, 1), filter 1s cubic-bezier(0.4, 0, 0.2, 1)';
        nextCard.style.transition = 'opacity 1s cubic-bezier(0.4, 0, 0.2, 1), transform 1s cubic-bezier(0.4, 0, 0.2, 1), filter 1s cubic-bezier(0.4, 0, 0.2, 1)';

        // Start animations in next frame
        requestAnimationFrame(() => {
          // Fade out current image with scale and blur
          currentCard.style.opacity = '0';
          currentCard.style.transform = 'scale(0.9)';
          currentCard.style.filter = 'blur(12px)';

          // Fade in next image with scale and blur
          nextCard.style.opacity = '1';
          nextCard.style.transform = 'scale(1)';
          nextCard.style.filter = 'blur(0px)';
        });

        // Clean up after animation completes
        setTimeout(() => {
          currentCard.style.zIndex = '1';
          isAnimatingRef.current = false;
        }, 1000);

        return nextIndex;
      });
    }, 4000); // Transition every 4 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <div className={styles.slideshowContainer}>
      <div className={styles.captionContainer}>
        <h3 className={styles.typingText}>
          {displayText}
          {isTyping && <span className={styles.cursor}>|</span>}
        </h3>
      </div>
      <div className={styles.slideshowWrapper}>
        {images.map((image, index) => (
          <div
            key={index}
            ref={(el) => (imageRefs.current[index] = el)}
            className={styles.imageCard}
          >
            <div className={styles.imageWrapper}>
              <img
                src={image}
                alt={`Foundry ${index + 1}`}
                className={styles.image}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

